import { useEffect, useRef, useState } from "react";

import * as Sentry from "@sentry/react";
import { Onfido } from "onfido-sdk-ui";
import type { SdkErrorTypes } from "onfido-sdk-ui/types/shared/Error";
import type { SdkParameters } from "onfido-sdk-ui/types/shared/SdkParameters";
import { useHistory } from "react-router";

import { Button, Spinner, Text, useModal } from "@alpacahq/ui/old";

import AmplitudeProvider from "~/AmplitudeProvider";
import { useMutationRequest } from "~/v2/api/hooks";
import type { Account } from "~/v2/api/rest/account";
import {
  createOnfidoApplicant,
  createOnfidoWorkflowRun,
  type PatchOnfidoParams,
  patchOnfidoSdkToken,
  updateOnfidoApplicant,
} from "~/v2/api/rest/onfido";
import { Path } from "~/v2/path";
import { eventTracker } from "~/v2/utils/eventTracker";

const RESTART_ONFIDO_ERROR_TYPES: readonly SdkErrorTypes[] = [
  "user_consent_denied",
  "workflow_abandoned",
];

const SETUP_ERROR_MESSAGE =
  "We couldn't start identity verification. Please try again.";

const ONFIDO_THEME: SdkParameters["theme"] = {
  name: "light",
  config: {
    borderRadiusButton: "8px",
    fontWeightBody: 400,
    fontSizeBody: "14px",
    fontSizeSubtitle: "16px",
    fontSizeTitle: "26px",
    colorContentButtonPrimaryText: "#000000",
    colorBackgroundButtonPrimary: "#FFD700",
    colorBackgroundButtonPrimaryHover: "#ffd900ba",
    colorBackgroundButtonPrimaryActive: "#c4a700",
  },
};

type OnfidoWorkflowPageProps = {
  account: Account;
  handleCompleted: () => void;
};

const OnfidoWorkflowPage = ({
  account,
  handleCompleted,
}: OnfidoWorkflowPageProps) => {
  const history = useHistory();
  const accountId = account.id;
  const onfidoApplicantId = account.onfido_applicant_id;
  const onfidoDetailsApplicantId = account.onfido_details?.applicant_id;

  const [isCompleted, setIsCompleted] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [setupErrorMessage, setSetupErrorMessage] = useState<string | null>(
    null,
  );
  const [workflowData, setWorkflowData] = useState<{
    token: string;
    workflowRunId: string;
  } | null>(null);

  const onfidoHandleRef = useRef<ReturnType<typeof Onfido.init> | null>(null);
  const hasStartedInitialFlow = useRef(false);
  const onfidoApplicantIdRef = useRef(onfidoApplicantId);

  useEffect(() => {
    onfidoApplicantIdRef.current = onfidoApplicantId;
  }, [onfidoApplicantId]);

  const captureWorkflowException = (
    error: unknown,
    extraTags?: Record<string, string | undefined>,
  ) => {
    Sentry.captureException(error, {
      tags: { flow: "onboarding", step: "onfido_workflow", ...extraTags },
    });
  };

  const showSetupError = (error: unknown) => {
    captureWorkflowException(error);
    setSetupErrorMessage(SETUP_ERROR_MESSAGE);
    setWorkflowData(null);
  };

  const handleExitApplication = () => {
    AmplitudeProvider.dispatch(
      "onfido_abandoned",
      JSON.stringify({ onfido_id: onfidoApplicantIdRef.current }),
    );
    history.push(Path.ROUTE_DASHBOARD);
  };

  const { mutate: patchSDKToken } = useMutationRequest(
    (body: PatchOnfidoParams) => patchOnfidoSdkToken(body),
  );

  const {
    mutate: createApplicant,
    isPending: isCreatingApplicant,
    isSuccess: isOnfidoCreated,
  } = useMutationRequest<Account, string>(
    (accountID) => createOnfidoApplicant(accountID),
    { onError: showSetupError },
  );

  const {
    mutate: updateApplicant,
    isPending: isUpdatingApplicant,
    isSuccess: isOnfidoUpdated,
  } = useMutationRequest<Account, string>(
    (accountID) => updateOnfidoApplicant(accountID),
    { onError: showSetupError },
  );

  const { mutate: initiateWorkflowRun, isPending: isCreatingWorkflow } =
    useMutationRequest(
      (accountID: string) => createOnfidoWorkflowRun(accountID),
      {
        onSuccess: (data) => {
          if (!data?.onfido_sdk_token || !data?.onfido_workflow_run_id) {
            showSetupError(new Error("workflow_run_response_incomplete"));
            return;
          }
          setSetupErrorMessage(null);
          setWorkflowData({
            token: data.onfido_sdk_token,
            workflowRunId: data.onfido_workflow_run_id,
          });
        },
        onError: showSetupError,
      },
    );

  const hasApplicant = !!onfidoApplicantId || !!onfidoDetailsApplicantId;
  const isApplicantReady = isOnfidoCreated || isOnfidoUpdated;

  const createOrUpdate = hasApplicant ? updateApplicant : createApplicant;

  const startWorkflowRun = () => {
    setSetupErrorMessage(null);
    initiateWorkflowRun(accountId);
  };

  const handleRestart = () => {
    if (onfidoHandleRef.current) {
      onfidoHandleRef.current.tearDown();
      onfidoHandleRef.current = null;
    }
    setSetupErrorMessage(null);
    setErrorMessage(null);
    setWorkflowData(null);

    if (!isApplicantReady) {
      hasStartedInitialFlow.current = false;
      createOrUpdate(accountId);
      return;
    }

    startWorkflowRun();
  };

  const userConsentErrorModal = useModal({
    title: "Identity Verification Required",
    content: (
      <div className="flex flex-col gap-4">
        <Text className="font-medium">We respect your privacy choices.</Text>
        <Text>
          However, to proceed with opening your account, we require your consent
          to process personal data and upload the necessary documents.
        </Text>
        <Text>
          This is essential to comply with legal and regulatory requirements.
        </Text>
      </div>
    ),
    controls: (
      <div className="flex w-full justify-between gap-4">
        <Button variant="red" onClick={handleExitApplication}>
          Exit Application
        </Button>
        <Button
          onClick={() => {
            userConsentErrorModal.toggle();
            handleRestart();
          }}
        >
          Restart Identity Verification
        </Button>
      </div>
    ),
  });

  const genericErrorModal = useModal({
    title: "Error",
    content: (
      <div className="flex flex-col gap-4">
        {errorMessage && <Text className="font-medium">{errorMessage}</Text>}
        <Text>
          An error occurred while verifying your identity. Please contact
          support at{" "}
          <a href="mailto:support@alpaca.markets">support@alpaca.markets</a> if
          the problem persists.
        </Text>
      </div>
    ),
    controls: (
      <div className="flex w-full justify-between gap-4">
        <Button variant="red" onClick={handleExitApplication}>
          Exit Application
        </Button>
        <Button
          onClick={() => {
            genericErrorModal.toggle();
            handleRestart();
          }}
        >
          Restart Identity Verification
        </Button>
      </div>
    ),
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: createOrUpdate is an unmemoized local function; listing it would re-run this effect every render and repeatedly create/update the applicant. Once per accountId is the intent.
  useEffect(() => {
    createOrUpdate(accountId);
  }, [accountId]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: startWorkflowRun is recreated every render, so listing it would restart the workflow run on every render. The hasStartedInitialFlow ref already guards this to one run.
  useEffect(() => {
    if (
      workflowData ||
      isCompleted ||
      setupErrorMessage ||
      hasStartedInitialFlow.current ||
      !isApplicantReady
    ) {
      return;
    }

    hasStartedInitialFlow.current = true;
    startWorkflowRun();
  }, [workflowData, isCompleted, isApplicantReady, setupErrorMessage]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: this effect mounts the Onfido workflow SDK and tears it down in its cleanup. captureWorkflowException, handleCompleted, patchSDKToken and the modal `toggle` methods are recreated every render, so listing them would tear down and re-mount the SDK on every render (the remount loop behind the earlier rollback). accountId is deliberately read from the closure for the same reason.
  useEffect(() => {
    if (!workflowData) {
      return;
    }

    const { token, workflowRunId } = workflowData;

    AmplitudeProvider.dispatch(
      "onfido_workflow_started",
      JSON.stringify({
        onfido_id: onfidoApplicantIdRef.current,
        workflow_run_id: workflowRunId,
      }),
    );

    eventTracker("Started Onfido Workflow Studio flow", {
      category: "Onboarding",
    });

    const handle = Onfido.init({
      token,
      containerId: "onfido-mount",
      workflowRunId,
      theme: ONFIDO_THEME,
      onComplete: () => {
        eventTracker("Finished Onfido Workflow Studio flow", {
          category: "Onboarding",
        });
        AmplitudeProvider.dispatch(
          "onfido_workflow_finished",
          JSON.stringify({
            onfido_id: onfidoApplicantIdRef.current,
            workflow_run_id: workflowRunId,
          }),
        );
        patchSDKToken({
          accountID: accountId,
          outcome: "USER_COMPLETED",
          token,
        });
        setIsCompleted(true);
        handleCompleted();
      },
      onError: (error) => {
        patchSDKToken({
          accountID: accountId,
          outcome: "SDK_ERROR",
          token,
        });
        captureWorkflowException(error, { error_type: error?.type });

        setWorkflowData(null);

        if (RESTART_ONFIDO_ERROR_TYPES.includes(error?.type)) {
          userConsentErrorModal.toggle();
          return;
        }

        setErrorMessage(error?.message ?? "An unexpected error occurred.");
        genericErrorModal.toggle();
      },
    } as SdkParameters);

    onfidoHandleRef.current = handle;

    return () => {
      handle.tearDown();
    };
  }, [workflowData]);

  const isSetupErrorVisible =
    !!setupErrorMessage && !workflowData && !isCompleted;
  const isLoading =
    !setupErrorMessage &&
    !workflowData &&
    !isCompleted &&
    (!isApplicantReady ||
      isCreatingApplicant ||
      isUpdatingApplicant ||
      isCreatingWorkflow);

  return (
    <div className="h-full">
      <div id="onfido-mount" className="h-full" />
      <div className="mt-4 mb-8 flex flex-col items-center justify-center gap-4">
        {isLoading && <Spinner />}
        {isSetupErrorVisible && (
          <div className="flex flex-col items-center gap-4 text-center">
            <Text>{setupErrorMessage}</Text>
            <Button onClick={handleRestart}>Retry Identity Verification</Button>
          </div>
        )}
        {isCompleted && <Text>Thank you! You will be redirected shortly.</Text>}
      </div>
      {userConsentErrorModal.component}
      {genericErrorModal.component}
    </div>
  );
};

export default OnfidoWorkflowPage;
